import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { User as UserIcon, LogOut, ChevronDown, BookOpen, Menu, X } from 'lucide-react';

export default function Navbar() {
  const [productOpen, setProductOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [user, setUser] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;

  // Sync user status on route change or localStorage update
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    } else {
      setUser(null);
    }
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('user');
    setUser(null);
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
    navigate('/');
  };

  const isActive = (path) => currentPath === path;
  const isParentActive = (prefix) => currentPath.startsWith(prefix);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileProductsOpen(false);
  };

  return (
    <div className="sticky top-0 z-50 transition-all duration-300">
      {/* Announcement Bar */}
      <div className="bg-[#071D2F] text-white text-xs font-medium py-2 overflow-hidden border-b border-slate-800">
        <div className="animate-marquee flex items-center shrink-0">
          {/* First Track */}
          <div className="flex items-center gap-8 shrink-0 pr-8">
            <span>Welvate cohort waitlist <span className="text-[#60A5FA] font-bold">NOW OPEN</span> · <span className="text-[#F59E0B] font-bold">Learn · Invest · Grow</span> ·</span>
            <span>Welvate cohort waitlist <span className="text-[#60A5FA] font-bold">NOW OPEN</span> · <span className="text-[#F59E0B] font-bold">Learn · Invest · Grow</span> ·</span>
            <span>Welvate cohort waitlist <span className="text-[#60A5FA] font-bold">NOW OPEN</span> · <span className="text-[#F59E0B] font-bold">Learn · Invest · Grow</span> ·</span>
            <span>Welvate cohort waitlist <span className="text-[#60A5FA] font-bold">NOW OPEN</span> · <span className="text-[#F59E0B] font-bold">Learn · Invest · Grow</span> ·</span>
          </div>

          {/* Duplicate Track for Endless Loop */}
          <div className="flex items-center gap-8 shrink-0 pr-8">
            <span>Welvate cohort waitlist <span className="text-[#60A5FA] font-bold">NOW OPEN</span> · <span className="text-[#F59E0B] font-bold">Learn · Invest · Grow</span> ·</span>
            <span>Welvate cohort waitlist <span className="text-[#60A5FA] font-bold">NOW OPEN</span> · <span className="text-[#F59E0B] font-bold">Learn · Invest · Grow</span> ·</span>
            <span>Welvate cohort waitlist <span className="text-[#60A5FA] font-bold">NOW OPEN</span> · <span className="text-[#F59E0B] font-bold">Learn · Invest · Grow</span> ·</span>
            <span>Welvate cohort waitlist <span className="text-[#60A5FA] font-bold">NOW OPEN</span> · <span className="text-[#F59E0B] font-bold">Learn · Invest · Grow</span> ·</span>
          </div>
        </div>
      </div>

      {/* Main Navbar Header */}
      <header className={`bg-[#FAF8F5] transition-all duration-300 ${scrolled ? 'shadow-md bg-[#FAF8F5]/95 backdrop-blur-md border-b border-gray-200/60' : 'border-b border-[#EBE8E1]'}`}>
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          
          {/* Brand Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="/logo_black2.png" 
              alt="Welvate" 
              className="h-20 sm:h-28 w-auto object-contain max-w-[200px]"
            />
          </Link>

          {/* Laptop Navigation Links (Desktop) */}
          <nav className="hidden lg:flex items-center gap-8 text-sm font-semibold">
            <Link to="/" className={`transition ${isActive('/') ? 'text-[#16325B] font-bold border-b-2 border-[#16325B] pb-1' : 'text-gray-800 hover:text-[#16325B]'}`}>
              Home
            </Link>

            <Link to="/about" className={`transition ${isActive('/about') ? 'text-[#16325B] font-bold border-b-2 border-[#16325B] pb-1' : 'text-gray-800 hover:text-[#16325B]'}`}>
              About Us
            </Link>

            {/* Products Dropdown Desktop */}
            <div 
              className="relative py-2 cursor-pointer"
              onMouseEnter={() => setProductOpen(true)}
              onMouseLeave={() => setProductOpen(false)}
            >
              <Link to="/products" className={`flex items-center gap-1 transition ${isParentActive('/products') ? 'text-[#16325B] font-bold border-b-2 border-[#16325B] pb-1' : 'text-gray-800 hover:text-[#16325B]'}`}>
                <span>Products</span>
                <span className="text-[10px]">▾</span>
              </Link>

              {productOpen && (
                <div className="absolute top-full left-0 w-64 bg-white rounded-2xl shadow-xl p-3 flex flex-col gap-1 border border-gray-100 z-50">
                  <Link to="/products/mutual-funds" className={`p-2.5 rounded-xl text-xs font-semibold ${isActive('/products/mutual-funds') ? 'bg-[#EEF4FA] text-[#16325B]' : 'text-gray-800 hover:bg-[#F0F5FA] hover:text-[#16325B]'}`}>Mutual Funds (MF)</Link>
                  <Link to="/products/sif" className={`p-2.5 rounded-xl text-xs font-semibold ${isActive('/products/sif') ? 'bg-[#EEF4FA] text-[#16325B]' : 'text-gray-800 hover:bg-[#F0F5FA] hover:text-[#16325B]'}`}>Specialized Investment Funds (SIF)</Link>
                  <Link to="/products/pms" className={`p-2.5 rounded-xl text-xs font-semibold ${isActive('/products/pms') ? 'bg-[#EEF4FA] text-[#16325B]' : 'text-gray-800 hover:bg-[#F0F5FA] hover:text-[#16325B]'}`}>Portfolio Management Services (PMS)</Link>
                  <Link to="/products/bonds-fds" className={`p-2.5 rounded-xl text-xs font-semibold ${isActive('/products/bonds-fds') ? 'bg-[#EEF4FA] text-[#16325B]' : 'text-gray-800 hover:bg-[#F0F5FA] hover:text-[#16325B]'}`}>Bonds / NCDs & FDs</Link>
                  <Link to="/products/aif" className={`p-2.5 rounded-xl text-xs font-semibold ${isActive('/products/aif') ? 'bg-[#EEF4FA] text-[#16325B]' : 'text-gray-800 hover:bg-[#F0F5FA] hover:text-[#16325B]'}`}>Alternate Investment Funds (AIF)</Link>
                  <Link to="/products/offshore" className={`p-2.5 rounded-xl text-xs font-semibold ${isActive('/products/offshore') ? 'bg-[#EEF4FA] text-[#16325B]' : 'text-gray-800 hover:bg-[#F0F5FA] hover:text-[#16325B]'}`}>International / Offshore</Link>
                  <Link to="/products/pre-ipo" className={`p-2.5 rounded-xl text-xs font-semibold ${isActive('/products/pre-ipo') ? 'bg-[#EEF4FA] text-[#16325B]' : 'text-gray-800 hover:bg-[#F0F5FA] hover:text-[#16325B]'}`}>Pre-IPO Unlisted</Link>
                  
                  <Link 
                    to="/products#insurance" 
                    onClick={() => setProductOpen(false)}
                    className="p-2.5 rounded-xl text-xs font-bold text-[#16325B] bg-[#EEF4FA] hover:bg-[#DBEAFE] transition flex items-center justify-between"
                  >
                    <span>Insurance (Health, Term & General)</span>
                    <span className="text-[10px]">🛡️</span>
                  </Link>
                </div>
              )}
            </div>

            <Link to="/courses" className={`transition ${isActive('/courses') ? 'text-[#16325B] font-bold border-b-2 border-[#16325B] pb-1' : 'text-gray-800 hover:text-[#16325B]'}`}>
              Courses
            </Link>

            <Link to="/faq" className={`transition ${isActive('/faq') ? 'text-[#16325B] font-bold border-b-2 border-[#16325B] pb-1' : 'text-gray-800 hover:text-[#16325B]'}`}>
              FAQ
            </Link>

            <Link to="/contact" className={`transition ${isActive('/contact') ? 'text-[#16325B] font-bold border-b-2 border-[#16325B] pb-1' : 'text-gray-800 hover:text-[#16325B]'}`}>
              Contact Us
            </Link>
          </nav>

          {/* Right Section: Desktop Auth State & Mobile Menu Button */}
          <div className="flex items-center gap-3 sm:gap-4">
            {/* Desktop Auth State */}
            <div className="hidden lg:flex items-center gap-4">
              {user ? (
                <div className="relative py-2" onMouseLeave={() => setUserMenuOpen(false)}>
                  <button 
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2.5 bg-[#EEF4FA] hover:bg-[#DBEAFE] text-[#0B2540] border border-[#C7D7E8] px-3.5 py-1.5 rounded-full transition shadow-sm"
                  >
                    <div className="w-7 h-7 rounded-full bg-[#16325B] text-white flex items-center justify-center font-bold text-xs uppercase">
                      {user.name ? user.name.charAt(0) : 'U'}
                    </div>
                    <span className="text-xs font-bold">{user.name}</span>
                    <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
                  </button>

                  {userMenuOpen && (
                    <div className="absolute top-full right-0 w-56 bg-white rounded-2xl shadow-xl p-2 border border-gray-100 z-50 space-y-1 mt-1">
                      <div className="px-3 py-2 border-b border-gray-100">
                        <p className="text-xs font-bold text-[#0B2540]">{user.name}</p>
                        <p className="text-[10px] text-gray-400 truncate">{user.email}</p>
                      </div>

                      <Link 
                        to="/profile" 
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2.5 p-2.5 rounded-xl text-xs font-semibold text-gray-700 hover:bg-[#F0F5FA] hover:text-[#16325B] transition"
                      >
                        <UserIcon className="w-4 h-4 text-[#16325B]" />
                        <span>My Profile</span>
                      </Link>

                      <Link 
                        to="/courses" 
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2.5 p-2.5 rounded-xl text-xs font-semibold text-gray-700 hover:bg-[#F0F5FA] hover:text-[#16325B] transition"
                      >
                        <BookOpen className="w-4 h-4 text-[#16325B]" />
                        <span>My Enrolled Courses</span>
                      </Link>

                      <button 
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2.5 p-2.5 rounded-xl text-xs font-semibold text-red-600 hover:bg-red-50 transition text-left"
                      >
                        <LogOut className="w-4 h-4 text-red-500" />
                        <span>Logout</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <Link to="/login" className="text-sm font-bold text-[#0B2540] hover:text-[#16325B] px-3 py-2 transition">
                    Login
                  </Link>
                  <Link to="/signup" className="bg-[#16325B] hover:bg-[#0F213D] text-white px-5 py-2 rounded-full font-bold text-sm transition shadow-sm">
                    Sign Up
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Hamburger Menu Toggle Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-[#0B2540] hover:text-[#16325B] focus:outline-none rounded-xl bg-gray-100/70"
              aria-label="Toggle Navigation Menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

        </div>

        {/* Mobile / Tablet Drawer Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-b border-gray-200 px-6 py-6 space-y-4 text-sm font-semibold shadow-2xl transition-all animate-in slide-in-from-top duration-200">
            <Link to="/" onClick={closeMobileMenu} className={`block py-1.5 ${isActive('/') ? 'text-[#16325B] font-bold' : 'text-gray-800'}`}>
              Home
            </Link>

            <Link to="/about" onClick={closeMobileMenu} className={`block py-1.5 ${isActive('/about') ? 'text-[#16325B] font-bold' : 'text-gray-800'}`}>
              About Us
            </Link>

            {/* Mobile Accordion for Products */}
            <div className="py-1">
              <button
                onClick={() => setMobileProductsOpen(!mobileProductsOpen)}
                className="w-full flex items-center justify-between py-1.5 text-gray-800 font-semibold focus:outline-none"
              >
                <span className={isParentActive('/products') ? 'text-[#16325B] font-bold' : ''}>Products</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileProductsOpen ? 'rotate-180 text-[#16325B]' : 'text-gray-500'}`} />
              </button>

              {mobileProductsOpen && (
                <div className="pl-4 pt-2 pb-1 space-y-2 border-l-2 border-[#16325B]/30 ml-2 mt-1">
                  <Link to="/products/mutual-funds" onClick={closeMobileMenu} className="block py-1 text-xs text-gray-700 hover:text-[#16325B]">Mutual Funds (MF)</Link>
                  <Link to="/products/sif" onClick={closeMobileMenu} className="block py-1 text-xs text-gray-700 hover:text-[#16325B]">Specialized Investment Funds (SIF)</Link>
                  <Link to="/products/pms" onClick={closeMobileMenu} className="block py-1 text-xs text-gray-700 hover:text-[#16325B]">Portfolio Management Services (PMS)</Link>
                  <Link to="/products/bonds-fds" onClick={closeMobileMenu} className="block py-1 text-xs text-gray-700 hover:text-[#16325B]">Bonds / NCDs & FDs</Link>
                  <Link to="/products/aif" onClick={closeMobileMenu} className="block py-1 text-xs text-gray-700 hover:text-[#16325B]">Alternate Investment Funds (AIF)</Link>
                  <Link to="/products/offshore" onClick={closeMobileMenu} className="block py-1 text-xs text-gray-700 hover:text-[#16325B]">International / Offshore</Link>
                  <Link to="/products/pre-ipo" onClick={closeMobileMenu} className="block py-1 text-xs text-gray-700 hover:text-[#16325B]">Pre-IPO Unlisted</Link>
                  <Link to="/products#insurance" onClick={closeMobileMenu} className="block py-1 text-xs text-[#16325B] font-bold">Insurance (Health, Term & General) 🛡️</Link>
                </div>
              )}
            </div>

            <Link to="/courses" onClick={closeMobileMenu} className={`block py-1.5 ${isActive('/courses') ? 'text-[#16325B] font-bold' : 'text-gray-800'}`}>
              Courses
            </Link>

            <Link to="/faq" onClick={closeMobileMenu} className={`block py-1.5 ${isActive('/faq') ? 'text-[#16325B] font-bold' : 'text-gray-800'}`}>
              FAQ
            </Link>

            <Link to="/contact" onClick={closeMobileMenu} className={`block py-1.5 ${isActive('/contact') ? 'text-[#16325B] font-bold' : 'text-gray-800'}`}>
              Contact Us
            </Link>

            {/* Mobile Auth Buttons / Profile Info */}
            <div className="pt-4 border-t border-gray-100">
              {user ? (
                <div className="space-y-3 bg-[#FAF8F5] p-4 rounded-2xl border border-gray-200/80">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#16325B] text-white flex items-center justify-center font-bold text-xs uppercase">
                      {user.name ? user.name.charAt(0) : 'U'}
                    </div>
                    <div>
                      <p className="text-xs font-bold text-[#0B2540]">{user.name}</p>
                      <p className="text-[10px] text-gray-400 truncate">{user.email}</p>
                    </div>
                  </div>
                  <div className="pt-2 flex flex-col gap-2">
                    <Link to="/profile" onClick={closeMobileMenu} className="flex items-center gap-2 text-xs font-semibold text-gray-700 py-1.5">
                      <UserIcon className="w-4 h-4 text-[#16325B]" />
                      <span>My Profile</span>
                    </Link>
                    <Link to="/courses" onClick={closeMobileMenu} className="flex items-center gap-2 text-xs font-semibold text-gray-700 py-1.5">
                      <BookOpen className="w-4 h-4 text-[#16325B]" />
                      <span>My Enrolled Courses</span>
                    </Link>
                    <button onClick={handleLogout} className="flex items-center gap-2 text-xs font-bold text-red-600 py-1.5">
                      <LogOut className="w-4 h-4 text-red-500" />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <Link to="/login" onClick={closeMobileMenu} className="w-1/2 text-center py-2.5 border border-gray-300 rounded-full font-bold text-xs text-[#0B2540]">
                    Login
                  </Link>
                  <Link to="/signup" onClick={closeMobileMenu} className="w-1/2 text-center py-2.5 bg-[#16325B] hover:bg-[#0F213D] text-white rounded-full font-bold text-xs shadow-sm">
                    Sign Up
                  </Link>
                </div>
              )}
            </div>

          </div>
        )}
      </header>
    </div>
  );
}